import { SoftwareInventoryService } from 'src/app/service/software-inventory.service';
import { Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, ViewChild, NgZone, OnDestroy, TemplateRef, ViewContainerRef } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { ProductDownloadInfo } from 'src/app/model/DownloadStatus';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';
import { Constraint } from 'src/app/model/Plan';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';

@Component({
    selector: 'app-prod-files-typeahead',
    templateUrl: './prod-files-typeahead.component.html',
    styleUrls: ['./prod-files-typeahead.component.css'],
    standalone: false
})
export class ProdFilesTypeaheadComponent implements OnInit, OnDestroy {
  queryText: string = "";
  queryTextChanged = new Subject<string>();
  prodFiles: ProductDownloadInfo[] = [];
  queryTextSubscription: Subscription;
  showList: boolean = false;
  selectedProdFile: ProductDownloadInfo;
  scrollHeight: string = "";
  overlayRef: OverlayRef;

  @ViewChild('prodFileInputText') prodFileInputText: ElementRef;
  @ViewChild('dropdownMenu') dropdownMenuTemplate: TemplateRef<any>;
  
  @Input()selectedProdFileName:string = "";
  @Input()constraints:Constraint[];
  @Input() identifier: string = '';

  @Output() onSelectFile: EventEmitter<ProductDownloadInfo> = new EventEmitter();
  @Output() onDeselectFile: EventEmitter<void> = new EventEmitter();
  
  constructor(private softInvServ: SoftwareInventoryService,
              private eleRef: ElementRef,
              private overlay: Overlay,
              private viewContainerRef: ViewContainerRef,
              private ngZone: NgZone) { }

  ngOnInit(): void {
    this.queryTextSubscription = this.queryTextChanged.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      tap(query  => {
        if(query != ''){
          this.getProdFile(query);
        }
      }),
    ).subscribe();
  }

  ngOnDestroy(): void {
    this.queryTextSubscription.unsubscribe();
    if (this.overlayRef) {
      this.overlayRef.dispose();
    }
  }

  onQueryTextChanged(query:string){
    this.queryTextChanged.next(query);
  }

  onFocus(): void {
    this.getProdFile(this.queryText);
  }
  
  onBlur(): void {
    this.showList = false;
    if (this.overlayRef) {
      this.overlayRef.detach();
    }
  }

  onSelect(prodFile: ProductDownloadInfo): void {
    this.showList = false;
    this.queryText = "";
    this.selectedProdFile = prodFile;
    this.selectedProdFileName = this.selectedProdFile.name;
    if (this.overlayRef) {
      this.overlayRef.detach();
    }
    this.onSelectFile.emit(prodFile);
  }

  removeSelectedFile(): void {
    this.onDeselectFile.emit();
    this.selectedProdFile = undefined;
    this.selectedProdFileName = '';
  }

  getProdFile(query:string): void {
    this.prodFiles = [];
      this.showList = false;
      this.softInvServ.getDownloadProductInfoByName(query.trim(), this.constraints).pipe(
        tap(resp => {
          this.prodFiles = resp.body;
          this.showList = true;
          if(this.prodFiles.length == 0){
            this.scrollHeight = '40px';
          }else if(this.prodFiles.length < 3){
            this.scrollHeight = this.prodFiles.length * 40 + 'px';
          }else {
            this.scrollHeight = '110px';
          }
          this.attachDropdownPortal();
        })
      ).subscribe();
  }

  attachDropdownPortal(): void {
    this.ngZone.run(() => {
      if (!this.overlayRef) {
        // Get the input field's actual width
        const inputWidth = this.prodFileInputText.nativeElement.offsetWidth;
        
        const positionStrategy = this.overlay
          .position()
          .flexibleConnectedTo(this.prodFileInputText)
          .withPositions([
            {
              originX: 'start',
              originY: 'bottom',
              overlayX: 'start',
              overlayY: 'top',
              offsetY: 4
            },
            {
              originX: 'start',
              originY: 'top',
              overlayX: 'start',
              overlayY: 'bottom',
              offsetY: -4
            }
          ])
          .withViewportMargin(8);

        this.overlayRef = this.overlay.create({
          positionStrategy,
          scrollStrategy: this.overlay.scrollStrategies.reposition(),
          hasBackdrop: false,
          width: inputWidth,
          minWidth: inputWidth,
          maxWidth: inputWidth
        });

        // Set custom ID for the overlay pane
        this.overlayRef.overlayElement.id = 'app-prod-files-typeahead-overlay';
      }

      if (this.dropdownMenuTemplate && this.showList) {
        if (this.overlayRef.hasAttached()) {
          this.overlayRef.detach();
        }
        const portal = new TemplatePortal(
          this.dropdownMenuTemplate,
          this.viewContainerRef
        );
        this.overlayRef.attach(portal);
      }
    });
  }

  @HostListener("document:click", ["$event"])
  clickout($event) {
    if (!this.prodFileInputText.nativeElement.contains($event.target) && 
        !this.isClickInsideOverlay($event.target)){
      this.showList = false;
      this.queryText = "";
      if (this.overlayRef) {
        this.overlayRef.detach();
      }
    }
  }

  private isClickInsideOverlay(target: any): boolean {
    if (!this.overlayRef) return false;
    const overlayElement = this.overlayRef.overlayElement;
    return overlayElement && overlayElement.contains(target);
  }
}
